import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDown, ShieldCheck, FileSignature, Search as SearchIcon, Handshake } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { logWarn } from "@/lib/logger";

/**
 * Personal document vault. When `userId` is provided (admin view), fetches
 * `/admin/users/{userId}/documents`. Otherwise fetches the current user's
 * own `/me/documents`.
 */
export default function DocumentVault({ userId = null }) {
  const { lang, user } = useApp();
  const [data, setData] = useState({ ndnca: [], mandates: [], intermediation: [] });
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  const url = userId ? `/admin/users/${userId}/documents` : "/me/documents";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(url);
      setData({
        ndnca: d.ndnca || [],
        mandates: d.mandates || [],
        intermediation: d.intermediation || [],
      });
    } catch (e) { logWarn("vault load failed", e); }
    finally { setLoading(false); }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  const downloadNdnca = async (n) => {
    try {
      if (n.no_pdf) {
        toast.error(lang === "it"
          ? "NDNCA segnato come firmato ma nessun PDF archiviato."
          : "NDNCA flagged as signed but no PDF stored.");
        return;
      }
      // Priority:
      //   1) if a target userId was passed in props → admin viewing that user
      //   2) else if the current user is admin AND this NDNCA belongs to
      //      someone else → hit the admin endpoint for that owner
      //   3) fallback: /ndnca/pdf which returns the latest one owned by self
      let url;
      if (userId) {
        url = `/admin/users/${userId}/ndnca/${n.id}/pdf`;
      } else if (isAdmin && n.user_id && n.user_id !== user?.user_id) {
        url = `/admin/users/${n.user_id}/ndnca/${n.id}/pdf`;
      } else {
        url = "/ndnca/pdf";
      }
      const resp = await api.get(url, { responseType: "blob" });
      const link = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = link;
      a.download = `ndnca-${(n.id || "").slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(link);
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF error"); }
  };

  const downloadMandate = async (m) => {
    try {
      const resp = await api.get(`/mandates/${m.id}/pdf`, { responseType: "blob" });
      const link = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = link;
      a.download = `mandato-${m.mandate_type}-${(m.id || "").slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(link);
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF error"); }
  };

  const downloadContract = async (c) => {
    try {
      const resp = await api.get(`/matches/intermediation/${c.id}/pdf`, { responseType: "blob" });
      const link = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = link;
      a.download = `intermediazione-${(c.id || "").slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(link);
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF error"); }
  };

  const empty = !loading && data.ndnca.length === 0 && data.mandates.length === 0 && data.intermediation.length === 0;

  return (
    <Card className="bg-white border-[#E5E5E3] p-6" data-testid={userId ? `admin-vault-${userId}` : "my-vault"}>
      <div className="mb-5">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-1">
          {lang === "it" ? "Documenti firmati" : "Signed documents"}
        </p>
        <h3 className="text-2xl font-light text-[#1A1A1A]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          {lang === "it" ? "I miei documenti" : "My documents"}
        </h3>
        <p className="text-xs text-[#6B6B6B] mt-1">
          {lang === "it"
            ? "Scarica in qualsiasi momento gli NDNCA, i mandati e le intermediazioni firmate."
            : "Download at any time your signed NDNCAs, mandates and intermediation contracts."}
        </p>
      </div>

      {loading && <p className="text-sm text-[#6B6B6B] py-6 text-center">…</p>}
      {empty && (
        <p className="text-sm text-[#6B6B6B] py-8 text-center" data-testid="vault-empty">
          {lang === "it" ? "Nessun documento firmato." : "No signed document yet."}
        </p>
      )}

      {data.ndnca.length > 0 && (
        <section className="mb-4">
          <p className="text-xs tracking-[0.25em] uppercase text-[#8A8A8A] mb-2">NDNCA</p>
          <div className="space-y-2">
            {data.ndnca.map((n) => (
              <div key={n.id} data-testid={`vault-ndnca-${n.id}`}
                   className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#1A1A1A] flex items-center gap-1.5">
                    <ShieldCheck className={`h-4 w-4 ${n.no_pdf ? "text-amber-500" : "text-emerald-600"}`} />
                    {n.signer_name || "—"}
                    {n.legacy && !n.no_pdf && <Badge className="bg-slate-100 text-slate-600 border border-slate-200 ms-1">legacy</Badge>}
                    {n.no_pdf && <Badge className="bg-amber-100 text-amber-700 border border-amber-200 ms-1">
                      {lang === "it" ? "senza PDF" : "no PDF"}
                    </Badge>}
                  </p>
                  {isAdmin && !userId && n.signer_email && n.user_id !== user?.user_id && (
                    <p className="text-[11px] text-[#8A8A8A] font-mono">{n.signer_email}</p>
                  )}
                  <p className="text-[11px] text-[#8A8A8A] font-mono">
                    {n.signed_at ? new Date(n.signed_at).toLocaleString() : "—"} · {(n.language || "it").toUpperCase()}
                  </p>
                </div>
                {!n.no_pdf && (
                  <Button size="sm" onClick={() => downloadNdnca(n)}
                          data-testid={`vault-ndnca-pdf-${n.id}`}
                          className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full">
                    <FileDown className="h-3.5 w-3.5 me-1.5" /> PDF
                  </Button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {data.mandates.length > 0 && (
        <section className="mb-4">
          <p className="text-xs tracking-[0.25em] uppercase text-[#8A8A8A] mb-2">
            {lang === "it" ? "Mandati / Ricerche" : "Mandates / Searches"}
          </p>
          <div className="space-y-2">
            {data.mandates.map((m) => {
              const isSale = m.mandate_type === "sale";
              return (
                <div key={m.id} data-testid={`vault-mandate-${m.id}`}
                     className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#1A1A1A] flex items-center gap-1.5">
                      {isSale ? <FileSignature className="h-4 w-4 text-[#C5A880]" /> : <SearchIcon className="h-4 w-4 text-[#C5A880]" />}
                      {isSale ? (m.property_ref || "Mandato di vendita") : (m.search_area || "Mandato di ricerca")}
                    </p>
                    <p className="text-[11px] text-[#8A8A8A] font-mono">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"} · <Badge className="ms-1 bg-[#EDE4CF] text-[#5A4A2E]">{m.mandate_type}</Badge>
                    </p>
                  </div>
                  <Button size="sm" onClick={() => downloadMandate(m)} data-testid={`vault-mandate-pdf-${m.id}`}
                          className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full">
                    <FileDown className="h-3.5 w-3.5 me-1.5" /> PDF
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {data.intermediation.length > 0 && (
        <section>
          <p className="text-xs tracking-[0.25em] uppercase text-[#8A8A8A] mb-2">
            {lang === "it" ? "Contratti di intermediazione" : "Intermediation contracts"}
          </p>
          <div className="space-y-2">
            {data.intermediation.map((c) => (
              <div key={c.id} data-testid={`vault-inter-${c.id}`}
                   className="rounded-lg border border-[#E5E5E3] bg-[#FAFAF8] p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#1A1A1A] flex items-center gap-1.5">
                    <Handshake className="h-4 w-4 text-[#C5A880]" />
                    {c.property_title || "—"}
                  </p>
                  <p className="text-[11px] text-[#8A8A8A] font-mono">
                    {c.id.slice(0, 8).toUpperCase()} · {c.buyer_signed ? "FIRMATO" : "IN ATTESA"}
                  </p>
                </div>
                <Button size="sm" onClick={() => downloadContract(c)} data-testid={`vault-inter-pdf-${c.id}`}
                        className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full">
                  <FileDown className="h-3.5 w-3.5 me-1.5" /> PDF
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
    </Card>
  );
}
